import { Injectable, inject } from '@angular/core';
import { Observable, map, throwError, catchError, of } from 'rxjs';
import { ApiService } from './api.service';
import { UserLoginResponse } from '@core/models/auth.models';

/**
 * Result of an authentication attempt.
 *
 * - `ok` successful operation; for login, `user_id` / `api_id` are populated.
 * - `error` with a `status` field that carries the server's status string
 *   verbatim (e.g. "invalid_username_or_password", "not_activated",
 *   "email_in_use") so the UI can decide what to show. Two special values
 *   are added by this service for transport-level problems:
 *     - "bad_data"         (response was not parseable JSON)
 *     - "connection_error" (HTTP-level failure)
 *   These match the constants used by SaltwaterUpRestClient.m.
 */
export interface AuthSuccess {
  ok: true;
  user_id?: number;
  api_id?: string;
}

export interface AuthFailure {
  ok: false;
  status: string;
}

export type AuthResult = AuthSuccess | AuthFailure;

/**
 * High-level auth operations built on top of ApiService.
 *
 * All four flows hit the same endpoint (POST /apiv1/users.json) and
 * differ only in the `action` form field and which response fields are
 * meaningful. We centralize the result-shape normalization here so that
 * NGXS actions and page components can deal in a uniform AuthResult type
 * rather than poking at raw response JSON.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = inject(ApiService);

  login(email: string, password: string): Observable<AuthResult> {
    return this.api
      .postUsers({ action: 'login', username: email, password })
      .pipe(
        map((res) => this.toLoginResult(res)),
        catchError(() => of({ ok: false, status: 'connection_error' } as AuthResult)),
      );
  }

  register(email: string, password: string): Observable<AuthResult> {
    return this.api
      .postUsers({ action: 'register', username: email, password })
      .pipe(
        map((res) => this.toStatusOnlyResult(res)),
        catchError(() => of({ ok: false, status: 'connection_error' } as AuthResult)),
      );
  }

  resetPassword(email: string, newPassword: string): Observable<AuthResult> {
    return this.api
      .postUsers({
        action: 'reset_password',
        username: email,
        new_password: newPassword,
      })
      .pipe(
        map((res) => this.toStatusOnlyResult(res)),
        catchError(() => of({ ok: false, status: 'connection_error' } as AuthResult)),
      );
  }

  resendActivation(email: string, password: string): Observable<AuthResult> {
    return this.api
      .postUsers({ action: 'resend_activation', username: email, password })
      .pipe(
        map((res) => this.toStatusOnlyResult(res)),
        catchError(() => of({ ok: false, status: 'connection_error' } as AuthResult)),
      );
  }

  /**
   * Shared response-status extractor, port of Config.m's
   * `-getStringValForKey:fromRestResults:`. Handles missing / null
   * `userLogin` by returning empty string, which we then classify as
   * "bad_data".
   */
  private extractStatus(res: UserLoginResponse): string {
    if (!res || !res.userLogin) {
      return '';
    }
    return res.userLogin.status ?? '';
  }

  private toLoginResult(res: UserLoginResponse): AuthResult {
    const status = this.extractStatus(res);
    if (status === 'success') {
      const user_id = res.userLogin?.user_id ? parseInt(res.userLogin.user_id, 10) : undefined;
      const api_id = res.userLogin?.api_id ?? undefined;
      return { ok: true, user_id, api_id };
    }
    return { ok: false, status: status || 'bad_data' };
  }

  private toStatusOnlyResult(res: UserLoginResponse): AuthResult {
    const status = this.extractStatus(res);
    if (status === 'success') {
      return { ok: true };
    }
    return { ok: false, status: status || 'bad_data' };
  }
}
